// Builds a playable tactical area from a POI coordinate + radius: the local
// slice of the road graph inside the circle, the road crossings that become
// legal entry/exit points, and the building footprints inside it.
//
// Reuses buildTacticalCorridors()/distanceInMeters() from road-graph.mjs —
// already builds the junction-to-junction corridor abstraction we need, this
// module only filters it to a circle and bisects each boundary-straddling
// corridor segment to find the crossing point.

import { buildTacticalCorridors, distanceInMeters } from "../road-graph.mjs";

export function buildTacticalZone({ zoneId, centerCoordinate, radiusMeters, roadGraph, buildingCollection }) {
  if (!Array.isArray(centerCoordinate)) throw new Error("buildTacticalZone requires a centerCoordinate.");
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) throw new Error("buildTacticalZone requires a positive radiusMeters.");

  const fullTactical = buildTacticalCorridors(roadGraph);
  const corridors = new Map();
  const boundaryCrossings = [];

  for (const corridor of fullTactical.corridors.values()) {
    const distances = corridor.coordinates.map((coordinate) => distanceInMeters(coordinate, centerCoordinate));
    const anyInside = distances.some((distance) => distance <= radiusMeters);
    const anyOutside = distances.some((distance) => distance > radiusMeters);
    if (!anyInside && !anyOutside) continue; // fully degenerate corridor, skip
    if (!anyInside) continue; // entirely outside the circle, not part of the zone

    corridors.set(corridor.id, corridor);

    if (anyOutside) {
      // Crosses the boundary somewhere along its length: find where.
      const crossing = findBoundaryCrossing(corridor.coordinates, centerCoordinate, radiusMeters);
      if (crossing) boundaryCrossings.push({ corridorId: corridor.id, crossingCoordinate: crossing });
    }
  }

  const buildings = filterBuildingsInCircle(buildingCollection, centerCoordinate, radiusMeters);

  return {
    zoneId: zoneId ?? `zone:${centerCoordinate[0]},${centerCoordinate[1]},${radiusMeters}`,
    centerCoordinate: [...centerCoordinate],
    radiusMeters,
    corridors,
    boundaryCrossings,
    buildings,
  };
}

// Walk the corridor's polyline segment by segment and find the point nearest
// the circle boundary on the segment that straddles it (one endpoint inside,
// the other outside). Approximate: returns the inside/outside straddling
// segment's midpoint-ish crossing via linear interpolation on distance, which
// is accurate enough for picking an entry road, not for physics.
function findBoundaryCrossing(coordinates, centerCoordinate, radiusMeters) {
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const start = coordinates[index];
    const end = coordinates[index + 1];
    const startDistance = distanceInMeters(start, centerCoordinate);
    const endDistance = distanceInMeters(end, centerCoordinate);
    const straddles = (startDistance <= radiusMeters) !== (endDistance <= radiusMeters);
    if (!straddles) continue;
    // Binary-search along the segment for the crossing point — cheap and
    // avoids assuming linear distance-vs-position (it isn't, exactly, but
    // converges fast over short road segments).
    let lower = 0;
    let upper = 1;
    for (let step = 0; step < 20; step += 1) {
      const mid = (lower + upper) / 2;
      const point = [start[0] + (end[0] - start[0]) * mid, start[1] + (end[1] - start[1]) * mid];
      const distance = distanceInMeters(point, centerCoordinate);
      if ((distance <= radiusMeters) === (startDistance <= radiusMeters)) lower = mid;
      else upper = mid;
    }
    const mid = (lower + upper) / 2;
    return [start[0] + (end[0] - start[0]) * mid, start[1] + (end[1] - start[1]) * mid];
  }
  return null;
}

function ringCentroid(ring) {
  let x = 0;
  let y = 0;
  for (const [lon, lat] of ring) {
    x += lon;
    y += lat;
  }
  return [x / ring.length, y / ring.length];
}

function featureCentroid(feature) {
  const geometry = feature?.geometry;
  if (!geometry) return null;
  if (geometry.type === "Polygon") return ringCentroid(geometry.coordinates[0]);
  if (geometry.type === "MultiPolygon") return ringCentroid(geometry.coordinates[0][0]);
  return null;
}

// Centroid-in-circle only for v1 — buildings are interact-targets/decoration,
// not nav obstacles yet, so true polygon containment isn't needed (see
// GAME_IDENTITY_V4.md / v4 implementation plan Phase 1 scope note).
function filterBuildingsInCircle(buildingCollection, centerCoordinate, radiusMeters) {
  const features = buildingCollection?.features || [];
  return features.filter((feature) => {
    const centroid = featureCentroid(feature);
    if (!centroid) return false;
    return distanceInMeters(centroid, centerCoordinate) <= radiusMeters;
  });
}

export function coordinateWithinZone(coordinate, zone) {
  return distanceInMeters(coordinate, zone.centerCoordinate) <= zone.radiusMeters;
}

// Distance from an arbitrary coordinate to the zone boundary polyline,
// exposed for extraction checks (a unit "reaches" a boundary crossing when
// it's within some tolerance of the crossing coordinate along its corridor).
export function distanceToNearestCrossing(coordinate, zone) {
  let nearest = Infinity;
  for (const crossing of zone.boundaryCrossings) {
    const distance = distanceInMeters(coordinate, crossing.crossingCoordinate);
    if (distance < nearest) nearest = distance;
  }
  return nearest;
}
